/**
 * The event notice channel's bounds.
 *
 * A notice is the room telling everybody in it that something happened — somebody picked an orb
 * up, somebody fired one at somebody, somebody came apart, somebody was eaten. It is *reporting*,
 * never world state: nothing here is hashed into `worldHash`, nothing here decides anything in
 * `step`, and a host that lost a notice has a reporting bug rather than a desync. Same contract
 * `skin`, `country` and a saying are under.
 *
 * **A NOTICE CARRIES RESOLVED NAMES, NEVER A FORMAT STRING.** The sayings rule, pointed the other
 * way: a saying is an id inbound because a client must not be able to choose words, and a notice
 * is words outbound because a client must not be handed a template to fill. Half of what a notice
 * says — a display name — is a value only the room knows, and a client assembling the sentence
 * itself would be a second copy of every rule about who may be named.
 *
 * **AND IT CARRIES NO POSITION.** Knowing that somebody took an orb is not knowing where they
 * took it — the roster frame's rule, and for the roster frame's reason: this channel is room-wide
 * rather than interest-scoped, so anything positional on it would be a wallhack rather than a
 * feed.
 */

import { TICK_RATE_HZ } from './simulation.js';

/**
 * The most notices one room may put on the wire in one tick, and therefore on one frame — the
 * two are the same number because there is at most one notice frame per room per tick.
 *
 * Six rather than a larger number because this is a *feed a person reads* over a moving arena,
 * not a log: a tick that produced more than six interesting things produced more than anybody can
 * take in, and the overflow is dropped rather than queued into the next tick. Dropping is the
 * right failure — a notice is about the moment it happened, so one delivered a second late is
 * worse than one not delivered at all.
 *
 * It is also the cap that keeps the channel affordable at the top end. A busy 200-player room
 * cannot spend more than six short sentences a tick on it, whatever the simulation does.
 */
export const MAX_NOTICES_PER_FRAME = 6;

/**
 * The fewest ticks that may pass between two notices about somebody coming apart — an
 * `ate_player` or a `shattered` — in one room. Three seconds at TICK_RATE_HZ.
 *
 * **A GAP RATHER THAN A SMALLER `MAX_NOTICES_PER_FRAME`, and the two are not alternatives.**
 * That cap is *per tick*: it bounds how much one frame may carry and says nothing whatever about
 * how often a frame arrives. The feed being "way too chatty" is a rate *across* ticks — a busy
 * room kills somebody several times a second, each death perfectly within the per-frame cap, and
 * what a reader gets is a column that scrolls faster than it can be read. Lowering the cap to one
 * would not change that at all; a minimum gap is the only thing that does.
 *
 * **IT COVERS THE TWO DEATH KINDS AND DELIBERATELY NOT THE TWO POWER-UP ONES.** `powerup_taken`
 * and `powerup_used` are the thing the feed was asked for in the first place — "it should display
 * which user collected what" — they are already rare (one orb every POWER_UP_MEAN_SPAWN_SECONDS,
 * at most MAX_POWER_UPS_PER_ROOM alive), and since a bot never picks an orb up they are
 * human-only by construction. Gating them would suppress the signal to quieten the noise.
 *
 * A notice this gap refuses is DROPPED and never queued, which is `MAX_NOTICES_PER_FRAME`'s own
 * overflow rule and for its reason: a notice is about the moment it happened, so one delivered
 * three seconds late is worse than one not delivered. It also means at most one death notice is
 * raised inside any one tick, which falls out of the gap rather than being a second rule.
 */
export const NOTICE_DEATH_GAP_TICKS = TICK_RATE_HZ * 3;
